import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Grid, Header, Button, Icon, Table, Label, Segment,
  Loader, Dimmer, Progress, Confirm
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import SwitchProfileModal from '../../components/SwitchProfileModal';
import ModelLogModal from '../../components/ModelLogModal';
import VRAMCheckModal from '../../components/VRAMCheckModal';

const statusMap = {
  0: { text: '已停止', color: 'grey', icon: 'stop' },
  1: { text: '启动中', color: 'yellow', icon: 'spinner' },
  2: { text: '运行中', color: 'green', icon: 'play' },
  3: { text: '错误', color: 'red', icon: 'warning sign' },
  4: { text: '停止中', color: 'orange', icon: 'spinner' },
};

const ModelManager = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vramModal, setVramModal] = useState({ open: false, modelId: null, modelName: '' });
  const [confirmAction, setConfirmAction] = useState({ open: false, modelId: null, action: '' });
  const [gpuAssignment, setGpuAssignment] = useState(null);
  const [switchModal, setSwitchModal] = useState(false);
  const [logModal, setLogModal] = useState({ open: false, modelId: null, modelName: '' });
  const [syncing, setSyncing] = useState(false);
  const [syncingIds, setSyncingIds] = useState(new Set());

  const loadModels = useCallback(async () => {
    try {
      const res = await API.get('/api/model-manager/models');
      const { success, data } = res.data;
      if (success) setModels(data || []);
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  }, []);

  const loadGPUAssignment = useCallback(async () => {
    try {
      const res = await API.get('/api/model-manager/gpu-assignment');
      const { success, data } = res.data;
      if (success) setGpuAssignment(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadModels();
    loadGPUAssignment();
    const timer = setInterval(loadModels, 15000);
    return () => clearInterval(timer);
  }, [loadModels, loadGPUAssignment]);

  const syncAllModels = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await API.post('/api/model-manager/models/sync-all');
      const { success, data, message } = res.data;
      if (success) {
        setModels(data.models || []);
        if (data.updated > 0) {
          showSuccess(`已同步 ${data.updated} 个模型状态`);
        }
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setSyncing(false);
  }, []);

  const syncSingleModel = useCallback(async (modelId) => {
    setSyncingIds(prev => new Set(prev).add(modelId));
    try {
      const res = await API.post(`/api/model-manager/models/${modelId}/sync`);
      const { success, data, message } = res.data;
      if (success) {
        setModels(prev => prev.map(m => m.id === modelId ? { ...m, status: data.status, status_message: data.status_message } : m));
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setSyncingIds(prev => { const s = new Set(prev); s.delete(modelId); return s; });
  }, []);

  const handleAction = async (modelId, action) => {
    try {
      const res = await API.post(`/api/model-manager/models/${modelId}/${action}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(`${action} 操作已发起`);
        loadModels();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setConfirmAction({ open: false, modelId: null, action: '' });
  };

  const getStatusInfo = (status) => statusMap[status] || statusMap[0];

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='cogs' /> 模型管理
            <Button.Group floated='right' size='mini'>
              <Button
                icon='sync alternate'
                title='同步 K8s Pod 实际状态'
                loading={syncing}
                onClick={syncAllModels}
              />
              <Button color='teal' onClick={() => setSwitchModal(true)}>
                <Icon name='exchange' /> 模式切换
              </Button>
              <Button primary onClick={() => navigate('/model-manager/add')}>
                <Icon name='plus' /> 注册模型
              </Button>
            </Button.Group>
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {loading ? (
            <Dimmer active inverted><Loader content='加载模型列表...' /></Dimmer>
          ) : models.length === 0 ? (
            <Segment placeholder textAlign='center'>
              <Header icon>
                <Icon name='database' />
                暂无注册模型
              </Header>
              <p>请通过 API 或管理面板注册模型</p>
            </Segment>
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <Table compact celled unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>模型名称</Table.HeaderCell>
                  <Table.HeaderCell className='hide-on-mobile'>显示名</Table.HeaderCell>
                  <Table.HeaderCell className='hide-on-mobile'>GPU</Table.HeaderCell>
                  <Table.HeaderCell className='hide-on-mobile'>TP</Table.HeaderCell>
                  <Table.HeaderCell className='hide-on-mobile'>显存(MB)</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell className='hide-on-mobile'>渠道ID</Table.HeaderCell>
                  <Table.HeaderCell>操作</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {models.map(m => {
                  const si = getStatusInfo(m.status);
                  return (
                    <Table.Row key={m.id}>
                      <Table.Cell>
                        <strong>{m.model_name}</strong>
                        {m.is_resident && <Label size='mini' color='blue' style={{ marginLeft: 4 }}>常驻</Label>}
                      </Table.Cell>
                      <Table.Cell className='hide-on-mobile'>{m.display_name}</Table.Cell>
                      <Table.Cell className='hide-on-mobile'>
                        <Label size='mini'>{m.gpu_indices || '-'}</Label>
                      </Table.Cell>
                      <Table.Cell className='hide-on-mobile'>{m.tensor_parallel}</Table.Cell>
                      <Table.Cell className='hide-on-mobile'>{m.expected_memory_mb}</Table.Cell>
                      <Table.Cell>
                        <Label color={si.color} size='small'>
                          <Icon name={si.icon} /> {si.text}
                        </Label>
                      </Table.Cell>
                      <Table.Cell className='hide-on-mobile'>{m.channel_id || '-'}</Table.Cell>
                      <Table.Cell>
                        <Button.Group size='mini'>
                          {m.status !== 2 && (
                            <Button
                              color='green'
                              icon='play'
                              title='启动'
                              onClick={() => setConfirmAction({ open: true, modelId: m.id, action: 'start' })}
                            />
                          )}
                          {m.status === 2 && (
                            <Button
                              color='red'
                              icon='stop'
                              title='停止'
                              onClick={() => setConfirmAction({ open: true, modelId: m.id, action: 'stop' })}
                            />
                          )}
                          <Button
                            color='blue'
                            icon='redo'
                            title='重启'
                            onClick={() => setConfirmAction({ open: true, modelId: m.id, action: 'restart' })}
                          />
                          <Button
                            icon='microchip'
                            title='显存预检'
                            onClick={() => setVramModal({ open: true, modelId: m.id, modelName: m.display_name || m.model_name })}
                          />
                          <Button
                            icon='file alternate outline'
                            title='日志'
                            onClick={() => setLogModal({ open: true, modelId: m.id, modelName: m.display_name || m.model_name })}
                          />
                          <Button
                            icon='sync'
                            title='同步 K8s Pod 实际状态'
                            loading={syncingIds.has(m.id)}
                            onClick={() => syncSingleModel(m.id)}
                          />
                          <Button
                            icon='edit'
                            title='编辑'
                            onClick={() => navigate(`/model-manager/edit/${m.id}`)}
                          />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* GPU Assignment Overview */}
      {gpuAssignment && gpuAssignment.gpus && gpuAssignment.gpus.length > 0 && (
        <Card fluid className='chart-card' style={{ marginTop: 16 }}>
          <Card.Content>
            <Card.Header className='header'>
              <Icon name='th' /> GPU 分配概览
            </Card.Header>
          </Card.Content>
          <Card.Content>
            <Grid columns={gpuAssignment.gpus.length <= 3 ? gpuAssignment.gpus.length : 3} stackable>
              {gpuAssignment.gpus.map(gpu => {
                const usedPct = gpu.memory_total > 0 ? ((gpu.memory_used / gpu.memory_total) * 100).toFixed(1) : 0;
                const assignedModels = (gpuAssignment.models || []).filter(
                  m => m.gpu_indices && m.gpu_indices.split(',').map(s => s.trim()).includes(String(gpu.index))
                );
                return (
                  <Grid.Column key={gpu.index}>
                    <Segment>
                      <Header as='h5'>GPU {gpu.index} - {gpu.name}</Header>
                      <Progress percent={parseFloat(usedPct)} size='small' color={parseFloat(usedPct) > 80 ? 'red' : 'blue'} progress>
                        {gpu.memory_used} / {gpu.memory_total} MB
                      </Progress>
                      {assignedModels.map(m => (
                        <Label key={m.id} size='small' color={m.status === 2 ? 'green' : 'grey'} style={{ margin: 2 }}>
                          {m.display_name || m.model_name}
                        </Label>
                      ))}
                      {assignedModels.length === 0 && <span style={{ color: '#999' }}>无模型分配</span>}
                    </Segment>
                  </Grid.Column>
                );
              })}
            </Grid>
          </Card.Content>
        </Card>
      )}

      <VRAMCheckModal
        open={vramModal.open}
        onClose={() => setVramModal({ open: false, modelId: null, modelName: '' })}
        modelId={vramModal.modelId}
        modelName={vramModal.modelName}
      />

      <Confirm
        open={confirmAction.open}
        header={`确认${confirmAction.action === 'start' ? '启动' : confirmAction.action === 'stop' ? '停止' : '重启'}模型`}
        content='确定要执行此操作吗？'
        confirmButton='确认'
        cancelButton='取消'
        onCancel={() => setConfirmAction({ open: false, modelId: null, action: '' })}
        onConfirm={() => handleAction(confirmAction.modelId, confirmAction.action)}
      />

      <SwitchProfileModal
        open={switchModal}
        onClose={() => setSwitchModal(false)}
      />

      <ModelLogModal
        open={logModal.open}
        onClose={() => setLogModal({ open: false, modelId: null, modelName: '' })}
        modelId={logModal.modelId}
        modelName={logModal.modelName}
      />
    </div>
  );
};

export default ModelManager;
